/**
 * The bell — a preview of the Inbox (F50 · decided 25 Sep: ONE unread number).
 *
 * It is the Inbox's ONLY way in — there is no sidebar entry (decided 25 Sep):
 * the panel's footer and "View archived" open the page.
 *
 * It used to be a separate notification centre that counted in-app cards in
 * the request tenant only, while the Inbox counted every unread message — cards
 * AND emails, from every company the person is related to — so the header said
 * 1 while the page said 3. Now the bell and the Inbox page read the same
 * query (`useInboxUnreadCount`) and the same rows (`useInbox`), and every
 * action — read, archive, mark all — goes through the Inbox API, which writes
 * each message's state where its kind keeps it. They cannot disagree.
 *
 * What the panel does:
 *  • Unread / All, over the Inbox's own views, newest first;
 *  • an in-app card keeps the expand-in-place disclosure (F49 · expanding marks
 *    it read) and opens its action; an email opens in the Inbox reader;
 *  • per-row "mark as read" and "archive" (with Undo) on hover or focus;
 *  • "Mark all as read" and "View archived" in the header menu;
 *  • "Open inbox" at the foot, always;
 *  • an Invitations tab while any company invitation or sponsorship offer
 *    waits for an answer — the ones put off with "Not now" included — with
 *    Review and Decline, so an ask is never only a Home card one click takes
 *    away (3 Oct 2026).
 *
 * The badge is what waits for the person (`useCitizenBellCount`): unread
 * messages plus the invitations and offers not put off. The Inbox's own unread number —
 * the Unread tab, the page — is unchanged.
 *
 * Anchors: in-app rows keep `notification-item-<notificationId>` (the id build
 * 44 and the notification API use); every row also carries its inbox id.
 */
import { useId, useState } from 'react';
import { Archive, Bell, CheckCheck, CheckCircle2, Inbox, MoreVertical } from '@tonnie/assets';
import { formatUnreadCount } from '@tonnie/foundation';
import { resolveNotificationCopy, useLocale, useTranslation } from '@tonnie/locale';
import {
  useCitizenBellCount,
  useDeclineInvitation,
  useInbox,
  useInboxAction,
  useMarkAllInboxRead,
  useMyInvitations,
  useMySponsorshipOffers,
  type MyPendingInvitation,
  type MySponsorshipOffer,
} from '@tonnie/sdk';
import type { InboxItem } from '@tonnie/types';
import type { TFunction } from 'i18next';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  NotificationDisclosure,
  PanelFooter,
  PanelFooterLinkContent,
  panelFooterLinkVariants,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  UnreadCount,
} from '@tonnie/ui';
import { toast } from 'sonner';
import { Link, useLocation } from 'wouter';

import { BellInvitationList } from '@/features/platform/invitations/components/BellInvitationList';
import { DeclineInvitationDialog } from '@/features/platform/invitations/components/DeclineInvitationDialog';
import { InvitationReviewDialog } from '@/features/platform/invitations/components/InvitationReviewDialog';
import { SponsorshipDeclineConfirm } from '@/features/platform/sponsorships/components/SponsorshipDeclineConfirm';
import { SponsorshipOfferDialog } from '@/features/platform/sponsorships/components/SponsorshipOfferDialog';

import { inboxHref } from '../hooks/use-inbox-page-state';
import { inboxPreview, inboxSenderName, inboxSubject } from './InboxListItem';
import { SenderAvatar } from './SenderAvatar';

/** The badge on the bell icon has room for one digit: above it, "9+". */
const BELL_BADGE_MAX = 9;
import { ShareRequestDeclineConfirm, ShareRequestDialog } from '@/features/platform/share-requests';

type BellTab = 'unread' | 'all' | 'invitations';

/** An in-app card's body, in the reader's language (the bell's F49 chain). */
function cardBody(t: TFunction, item: InboxItem): string {
  if (item.content.state !== 'in_app') return inboxPreview(t, item);
  return resolveNotificationCopy(t, {
    key: item.content.bodyKey ?? undefined,
    values: item.content.values ?? undefined,
    column: item.content.body,
  });
}

export function InboxBell() {
  const { t } = useTranslation();
  const { formatTimeAgo } = useLocale();
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<BellTab>('unread');

  const bell = useCitizenBellCount();
  const unreadCount = bell.unread;
  const { data: invitations = [] } = useMyInvitations();
  const { data: offers = [] } = useMySponsorshipOffers();
  // The tab exists only while there is something in it; the last answer
  // falls back to the messages.
  const hasAsks = invitations.length + offers.length > 0;
  const activeTab: BellTab = tab === 'invitations' && !hasAsks ? 'unread' : tab;
  const list = useInbox(activeTab === 'unread' ? 'unread' : 'inbox');
  const decline = useDeclineInvitation();
  // The invitation dialogs live outside the panel: opening one closes it.
  const [reviewing, setReviewing] = useState<MyPendingInvitation | null>(null);
  const [declining, setDeclining] = useState<MyPendingInvitation | null>(null);
  const [reviewingOffer, setReviewingOffer] = useState<MySponsorshipOffer | null>(null);
  const [decliningOffer, setDecliningOffer] = useState<MySponsorshipOffer | null>(null);
  const action = useInboxAction();
  const markAll = useMarkAllInboxRead();
  const items = list.data?.pages[0]?.items ?? [];

  /** In-app paths through the router; anything else is a full navigation. */
  const go = (href: string) => {
    setOpen(false);
    if (href.startsWith('/') && !href.startsWith('//')) setLocation(href);
    else window.location.assign(href);
  };

  const read = (item: InboxItem) => {
    if (!item.readAt) action.mutate({ id: item.id, action: 'read' });
  };

  /** A card goes where it points; an email opens in the Inbox reader. */
  const openItem = (item: InboxItem) => {
    if (item.content.state === 'in_app') {
      read(item);
      go(item.content.actionUrl ?? inboxHref('inbox', item.id));
      return;
    }
    go(inboxHref('inbox', item.id));
  };

  const archive = (item: InboxItem) =>
    action.mutate(
      { id: item.id, action: 'archive' },
      {
        onSuccess: () =>
          toast(t('inbox.archived'), {
            action: {
              label: t('inbox.undo'),
              onClick: () => action.mutate({ id: item.id, action: 'restore' }),
            },
          }),
        onError: () => toast.error(t('inbox.actionError')),
      }
    );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="notification-bell"
            data-test-unread={unreadCount}
            data-test-invitations={bell.invitationsWaiting}
            variant="ghost"
            size="icon"
            className="relative"
            // The accessible name carries the count (F49): the visual badge is a
            // number drawn on an icon. It says the count AS DRAWN ("9+"), so the
            // name contains the badge's text (WCAG 2.5.3).
            aria-label={
              bell.total === 0
                ? t('notifications.title')
                : bell.invitationsWaiting > 0
                  ? t('notifications.bellLabelNeedsYou', {
                      count: bell.total,
                      shown: formatUnreadCount(bell.total, BELL_BADGE_MAX),
                    })
                  : t('notifications.bellLabel', {
                      count: bell.total,
                      shown: formatUnreadCount(bell.total, BELL_BADGE_MAX),
                    })
            }
          >
            <Bell className="h-5 w-5" />
            <UnreadCount
              count={bell.total}
              max={BELL_BADGE_MAX}
              overlay
              aria-hidden="true"
              data-testid="notification-unread-badge"
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          data-testid="notification-panel"
          className="w-[26rem] max-w-[calc(100vw-1rem)] overflow-hidden p-0"
          align="end"
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-2 border-b border-border/50 px-4 py-3">
            <div className="min-w-0">
              <h3 className="font-semibold leading-tight">{t('inbox.title')}</h3>
              <p className="text-xs text-muted-foreground" aria-live="polite">
                {unreadCount > 0
                  ? t('inbox.unreadCount', { count: unreadCount })
                  : bell.invitationsWaiting > 0
                    ? t('inbox.invitationsWaiting', { count: bell.invitationsWaiting })
                    : t('inbox.allCaughtUp')}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="notification-kebab"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t('notifications.moreActions')}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  data-testid="notification-mark-all-read"
                  disabled={unreadCount === 0 || markAll.isPending}
                  onSelect={() =>
                    markAll.mutate(undefined, {
                      onError: () => toast.error(t('inbox.actionError')),
                    })
                  }
                >
                  <CheckCheck className="h-4 w-4" />
                  {t('inbox.markAllRead')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="notification-view-dismissed"
                  onSelect={() => go(inboxHref('archived'))}
                >
                  <Archive className="h-4 w-4" />
                  {t('inbox.viewArchived')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* ── Filter ─────────────────────────────────────────────────── */}
          {/* The filter is a tab strip over ONE panel — the list it filters. */}
          <Tabs value={activeTab} onValueChange={(v) => setTab(v as BellTab)} className="gap-0">
            <div className="px-4 pt-3">
              <TabsList className="w-full">
                <TabsTrigger
                  data-testid="notification-tab-unread"
                  value="unread"
                  className="flex-1"
                >
                  {t('inbox.tabUnread')}
                  <UnreadCount count={unreadCount} />
                </TabsTrigger>
                <TabsTrigger data-testid="notification-tab-all" value="all" className="flex-1">
                  {t('notifications.tabAll')}
                </TabsTrigger>
                {hasAsks && (
                  <TabsTrigger
                    data-testid="notification-tab-invitations"
                    value="invitations"
                    className="flex-1"
                  >
                    {t('notifications.tabInvitations')}
                    <UnreadCount count={bell.invitationsWaiting} />
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* ── Invitations ──────────────────────────────────────────── */}
            <TabsContent value="invitations">
              <ScrollArea className="h-[420px]">
                <BellInvitationList
                  invitations={invitations}
                  offers={offers}
                  onReview={(invitation) => {
                    setOpen(false);
                    setReviewing(invitation);
                  }}
                  onDecline={(invitation) => {
                    setOpen(false);
                    setDeclining(invitation);
                  }}
                  onReviewOffer={(offer) => {
                    setOpen(false);
                    setReviewingOffer(offer);
                  }}
                  onDeclineOffer={(offer) => {
                    setOpen(false);
                    setDecliningOffer(offer);
                  }}
                />
              </ScrollArea>
            </TabsContent>

            {/* ── Messages ─────────────────────────────────────────────── */}
            {/* One panel, filtered by Unread / All. */}
            {activeTab !== 'invitations' && (
              <TabsContent value={activeTab}>
                <ScrollArea className="h-[420px]">
                  {list.isLoading ? (
                    <div className="space-y-1 p-3" aria-busy="true" aria-label={t('inbox.loading')}>
                      {[0, 1, 2].map((row) => (
                        <div key={row} className="flex items-start gap-3 px-2 py-3">
                          <Skeleton className="h-9 w-9 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-3.5 w-2/5" />
                            <Skeleton className="h-3.5 w-4/5" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : list.isError ? (
                    <div className="flex h-[420px] flex-col items-center justify-center gap-3 p-8 text-center">
                      <p className="text-sm text-muted-foreground">{t('inbox.loadError')}</p>
                      <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
                        {t('inbox.retry')}
                      </Button>
                    </div>
                  ) : items.length > 0 ? (
                    <ul className="divide-y divide-border/50">
                      {items.map((item) => (
                        <BellRow
                          key={item.id}
                          item={item}
                          when={formatTimeAgo(item.sentAt)}
                          busy={action.isPending}
                          onOpen={openItem}
                          onRead={read}
                          onArchive={archive}
                        />
                      ))}
                    </ul>
                  ) : (
                    <div className="flex h-[420px] flex-col items-center justify-center p-8 text-center">
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                        {activeTab === 'unread' ? (
                          <CheckCheck className="h-6 w-6 text-primary-accessible" />
                        ) : (
                          <Inbox className="h-6 w-6 text-primary-accessible" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {activeTab === 'unread' ? t('inbox.emptyUnread') : t('inbox.emptyInbox')}
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            )}
          </Tabs>

          {/* ── Every message, email included, lives in the Inbox ───────── */}
          <PanelFooter>
            <Link
              href="/inbox"
              onClick={() => setOpen(false)}
              data-testid="notification-panel-open-inbox"
              className={panelFooterLinkVariants()}
            >
              <PanelFooterLinkContent icon={Inbox}>
                {t('inbox.openFromBell')}
              </PanelFooterLinkContent>
            </Link>
          </PanelFooter>
        </PopoverContent>
      </Popover>
      {/* A company's request for more (D2) has its own review and decline. */}
      {reviewing?.request ? (
        <ShareRequestDialog
          invitation={{ ...reviewing, request: reviewing.request }}
          open
          onOpenChange={(next) => !next && setReviewing(null)}
        />
      ) : reviewing ? (
        <InvitationReviewDialog
          invitation={reviewing}
          open
          onOpenChange={(next) => !next && setReviewing(null)}
        />
      ) : null}
      {declining?.request ? (
        <ShareRequestDeclineConfirm
          invitation={declining}
          open
          onOpenChange={(next) => !next && setDeclining(null)}
        />
      ) : declining ? (
        <DeclineInvitationDialog
          invitation={declining}
          open
          onOpenChange={(next) => !next && setDeclining(null)}
          decline={decline}
        />
      ) : null}
      {reviewingOffer && (
        <SponsorshipOfferDialog
          open
          onOpenChange={(next) => !next && setReviewingOffer(null)}
          sponsorshipId={reviewingOffer.id}
        />
      )}
      {decliningOffer && (
        <SponsorshipDeclineConfirm
          open
          onOpenChange={(next) => !next && setDecliningOffer(null)}
          sponsorshipId={decliningOffer.id}
          company={decliningOffer.companyName}
          keepsOwnAccess={decliningOffer.takesOverOwnJourney}
          onDeclined={() => setDecliningOffer(null)}
        />
      )}
    </>
  );
}

interface BellRowProps {
  item: InboxItem;
  when: string;
  busy: boolean;
  onOpen: (item: InboxItem) => void;
  onRead: (item: InboxItem) => void;
  onArchive: (item: InboxItem) => void;
}

function BellRow({ item, when, busy, onOpen, onRead, onArchive }: BellRowProps) {
  const { t } = useTranslation();
  const unread = !item.readAt;
  const card = item.content.state === 'in_app' ? item.content : null;
  // In-app rows keep the id the notification API uses.
  const rowId = card ? card.notificationId : item.id;
  const senderId = useId();

  return (
    <li
      data-testid={
        card ? `notification-item-${card.notificationId}` : `inbox-preview-item-${item.id}`
      }
      data-inbox-id={item.id}
      data-test-read={!unread}
      className={cn(
        'group relative flex items-start gap-3 py-3 pl-4 pr-2 transition-colors',
        'hover:bg-muted/60 has-[[data-row-open]:focus-visible]:bg-muted/60',
        unread && 'bg-primary/[0.04]'
      )}
    >
      {/* Opening the message is the row's own button, stretched over it; "show
          more" and the actions sit ABOVE it as siblings — no control is nested
          inside another (axe nested-interactive). */}
      <button
        type="button"
        data-row-open
        data-testid={`notification-open-${rowId}`}
        aria-label={inboxSubject(t, item)}
        aria-describedby={senderId}
        onClick={() => onOpen(item)}
        className="absolute inset-0 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      />
      {unread && (
        <span
          className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-primary"
          aria-hidden
        />
      )}
      <SenderAvatar sender={item.sender} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            id={senderId}
            className={cn(
              'min-w-0 flex-1 truncate text-xs',
              unread ? 'font-semibold text-foreground' : 'text-muted-foreground'
            )}
          >
            {inboxSenderName(item, t('inbox.fromPlatform'))}
          </span>
          <span className="shrink-0 text-2xs tabular-nums text-muted-foreground">{when}</span>
        </div>
        {/* Two lines of title, three of body, the rest one tap away — and
            expanding marks it read (D-30). */}
        <NotificationDisclosure
          domId={rowId}
          title={inboxSubject(t, item)}
          body={cardBody(t, item)}
          titleClassName={unread ? 'font-semibold' : 'font-medium'}
          expandLabel={t('notifications.showMore')}
          collapseLabel={t('notifications.showLess')}
          onExpand={unread ? () => onRead(item) : undefined}
        />
      </div>

      <div className="relative z-10 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        {unread && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-primary-accessible"
            aria-label={t('notifications.markReadAction')}
            disabled={busy}
            data-testid={`notification-mark-read-${rowId}`}
            onClick={() => onRead(item)}
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          aria-label={t('inbox.archive')}
          disabled={busy}
          data-testid={`notification-archive-${rowId}`}
          onClick={() => onArchive(item)}
        >
          <Archive className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}
